import React, { useEffect, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { getToken } from "@/lib/auth";

export default function AdminMessages() {
  const [users,     setUsers]     = useState([]);
  const [messages,  setMessages]  = useState([]);
  const [recipientId, setRecipientId] = useState("");
  const [subject,   setSubject]   = useState("");
  const [body,      setBody]      = useState("");
  const [sending,   setSending]   = useState(false);
  const [sendError, setSendError] = useState("");
  const [sendOk,    setSendOk]    = useState(false);
  const [loading,   setLoading]   = useState(true);

  const token = getToken();
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  useEffect(() => {
    Promise.all([
      fetch("/api/admin/users",    { headers }).then((r) => r.ok ? r.json() : []),
      fetch("/api/admin/messages", { headers }).then((r) => r.ok ? r.json() : []),
    ]).then(([u, m]) => {
      setUsers(u);
      setMessages(m);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (!recipientId) { setSendError("Select a recipient"); return; }
    if (!body.trim()) { setSendError("Message body is required"); return; }
    setSending(true); setSendError(""); setSendOk(false);
    try {
      const res = await fetch("/api/admin/message", {
        method: "POST",
        headers,
        body: JSON.stringify({ recipient_id: recipientId, subject: subject || undefined, body }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "Failed to send");
      setSendOk(true);
      setBody(""); setSubject(""); setRecipientId("");
      // Prepend to messages list
      setMessages((prev) => [d, ...prev]);
      // Refresh full list
      fetch("/api/admin/messages", { headers })
        .then((r) => r.ok ? r.json() : [])
        .then(setMessages)
        .catch(() => {});
    } catch (e) {
      setSendError(e.message);
    } finally {
      setSending(false);
    }
  };

  const recipientName = (id) => {
    const u = users.find((u) => u.id === id);
    return u ? (u.name || u.email || id) : id;
  };

  return (
    <AdminShell>
      <div className="px-6 md:px-10 py-10 text-white max-w-4xl">
        <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-2">Admin</div>
        <h1 className="font-display text-3xl mb-8">Message Users</h1>

        {/* Compose */}
        <div className="glass rounded-3xl p-7 mb-8">
          <div className="text-[11px] tracking-[0.24em] uppercase text-white/40 mb-5">Compose</div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* Recipient */}
            <div>
              <label className="text-[11px] tracking-[0.18em] uppercase text-white/40 block mb-2">Recipient</label>
              <select
                value={recipientId}
                onChange={(e) => setRecipientId(e.target.value)}
                style={{ colorScheme: "dark", backgroundColor: "#0b1220", color: "#ffffff" }}
                className="w-full bg-[#0b1220] border border-white/10 focus:border-[rgba(212,175,55,0.5)] outline-none rounded-xl px-3 py-3 text-white text-sm"
              >
                <option value="">Select a user…</option>
                {users.filter((u) => u.name || u.email).map((u) => (
                  <option key={u.id} value={u.id}>{u.name || u.email}</option>
                ))}
              </select>
            </div>
            {/* Subject */}
            <div>
              <label className="text-[11px] tracking-[0.18em] uppercase text-white/40 block mb-2">Subject <span className="text-white/25 normal-case">· optional</span></label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Message from BoraMoyo"
                className="w-full bg-white/[0.03] border border-white/10 focus:border-[rgba(212,175,55,0.5)] outline-none rounded-xl px-3 py-3 text-white text-sm placeholder:text-white/25"
              />
            </div>
          </div>

          {/* Body */}
          <div className="mb-5">
            <label className="text-[11px] tracking-[0.18em] uppercase text-white/40 block mb-2">Message</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your message here…"
              rows={5}
              className="w-full bg-white/[0.03] border border-white/10 focus:border-[rgba(212,175,55,0.5)] outline-none rounded-xl px-4 py-3 text-white text-sm placeholder:text-white/25 resize-none"
            />
          </div>

          {sendError && <p className="text-red-400 text-sm mb-3">{sendError}</p>}
          {sendOk    && <p className="text-emerald-400 text-sm mb-3">Message sent.</p>}

          <button
            onClick={send}
            disabled={sending}
            className="rounded-full bg-gradient-gold text-[#080B14] font-semibold px-7 py-3 glow-gold-soft disabled:opacity-40 text-sm"
          >
            {sending ? "Sending…" : "Send Message"}
          </button>
        </div>

        {/* History */}
        <div className="glass rounded-3xl p-7">
          <div className="text-[11px] tracking-[0.24em] uppercase text-white/40 mb-5">Sent Messages</div>
          {loading ? (
            <div className="text-white/40 text-sm py-6">Loading…</div>
          ) : messages.length === 0 ? (
            <div className="text-white/30 text-sm py-6 text-center">No messages sent yet.</div>
          ) : (
            <div className="space-y-3">
              {messages.map((m) => (
                <div key={m.message_id || m.created_at} className="border border-white/6 rounded-2xl px-5 py-4">
                  <div className="flex items-start justify-between gap-4 mb-1">
                    <div>
                      <span className="text-white/85 font-medium text-sm">{m.subject || "Message from BoraMoyo"}</span>
                      <span className="text-white/35 text-xs ml-3">→ {recipientName(m.recipient_id)}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {m.read
                        ? <span className="text-[11px] text-white/30">Read</span>
                        : <span className="text-[11px] text-[#F2DE94]/70">Unread</span>
                      }
                      <span className="text-white/25 text-xs">{m.created_at ? m.created_at.slice(0, 10) : ""}</span>
                    </div>
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed">{m.body}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
